import type { Feature, Point } from 'geojson';
import { useFiltersStore } from '@features/regional-overview/components/filters/filters.store';
import { useRegionalOverviewMock } from '@features/regional-overview/regional-risk/hooks/use-regional-overview-mock';
import { useGetAllRegionalOverviewResultsV2 } from 'querys/regional-overview/regional-overview';
import type { RegionalOverviewDigitalTrapsProperties } from 'querys/regional-overview/regional-overview.query.models';
import { RegionalOverviewSource } from 'querys/regional-overview/regional-overview.query.models';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDigitalTrapsStore } from '../digital-traps.zustand';
import { calculateTotalTraps, createTrapGroups, translateGroupInfo } from '../utils';

export const useDigitalTrapsData = () => {
  const { companyId } = useParams();
  const [t] = useTranslation();
  const { isRegionalOverViewMocked } = useRegionalOverviewMock();

  const selectedTrapId = useDigitalTrapsStore(state => state.selectedTrapId);
  const setSelectedTrap = useDigitalTrapsStore(state => state.setSelectedTrap);
  const setAllTrapGroups = useDigitalTrapsStore(state => state.setAllTrapGroups);
  const setTrapGroups = useDigitalTrapsStore(state => state.setTrapGroups);
  const setTotalOfTraps = useDigitalTrapsStore(state => state.setTotalOfTraps);
  const setIsLoading = useDigitalTrapsStore(state => state.setIsLoading);

  const setFeatures = useFiltersStore(state => state.setFeatures);
  const setContext = useFiltersStore(state => state.setContext);
  const filteredFeatures = useFiltersStore(state => state.filteredFeatures) as Feature<Point, RegionalOverviewDigitalTrapsProperties>[];

  const {
    features: allFeatures,
    isLoading,
    isError
  } = useGetAllRegionalOverviewResultsV2<Point, RegionalOverviewDigitalTrapsProperties>(
    RegionalOverviewSource.DIGITAL_TRAPS,
    '2025-05-06',
    companyId ?? '',
    isRegionalOverViewMocked
  );

  const localizedFeatures = useMemo(() => translateGroupInfo(allFeatures, t), [allFeatures, t]);

  const allTrapGroups = useMemo(() => createTrapGroups(localizedFeatures.features), [localizedFeatures]);

  useEffect(() => {
    setFeatures(localizedFeatures.features ?? []);
  }, [localizedFeatures, setFeatures]);

  useEffect(() => {
    setAllTrapGroups(allTrapGroups);
  }, [allTrapGroups, setAllTrapGroups]);

  useEffect(() => {
    setContext({ trapGroups: allTrapGroups });
  }, [allTrapGroups, setContext]);

  useEffect(() => {
    const groups = createTrapGroups(filteredFeatures);
    setTrapGroups(groups);
    setTotalOfTraps(calculateTotalTraps(groups));
  }, [filteredFeatures, setTotalOfTraps, setTrapGroups]);

  useEffect(() => {
    if (!selectedTrapId) {
      setSelectedTrap(undefined);
      return;
    }
    const trap = localizedFeatures.features.find(feature => feature.properties.id === selectedTrapId);
    setSelectedTrap(trap);
  }, [selectedTrapId, localizedFeatures, setSelectedTrap]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [setIsLoading, isLoading]);

  return { isLoading, isError };
};
